/**
 * A local throw whose `message` is meant to be shown to the user verbatim.
 *
 * Everything else — axios network failures, unexpected TypeErrors — keeps the
 * caller's fallback copy, because their messages were written for a developer
 * reading a stack trace, not for someone mid-task in a wizard.
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

/**
 * Extract a human-readable error message from an API error response.
 *
 * Handles both the legacy string format and the structured
 * `{ code, message }` format returned by `raise_app_error`.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as any)?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (
    detail &&
    typeof detail === 'object' &&
    typeof detail.message === 'string'
  )
    return detail.message;

  // A UserFacingError is a LOCAL throw whose message was written FOR the user.
  // `buildStrategyConfig` refuses a Short-Only structure with a sentence saying
  // exactly what to change, and every caller flattened it to its own generic
  // fallback — so the one error that already knew the answer was the one the
  // user could not read.
  //
  // Opt-in by class, NOT by shape. Axios reports a network failure as a plain
  // `new Error('Network Error')`, which is indistinguishable from a deliberate
  // message by inspection and is strictly worse than the caller's copy. Only an
  // error a human wrote for this purpose gets through.
  if (error instanceof UserFacingError) return error.message;

  return fallback;
}

/**
 * True when the API rejected the request as *invalid input* (HTTP 400/422) —
 * i.e. the user's configuration is genuinely wrong and adjusting the inputs
 * would fix it. A 404 (endpoint not deployed), 5xx, or a network failure is NOT
 * an input problem, so callers can avoid the misleading "your config is invalid"
 * copy for what is really a server / deploy / connectivity error.
 */
export function isInputValidationError(error: unknown): boolean {
  const status = (error as any)?.response?.status;
  return status === 400 || status === 422;
}

/**
 * The structured `data` an API refusal carries alongside its sentence.
 *
 * `APIException(client_data=...)` reaches the browser as `detail.data`
 * (`errors/handlers.py` sets it, and only when it is non-empty). It is the one
 * machine-readable part of a refusal written FOR the user: `internal_context`
 * carries the rest and never leaves the server.
 *
 * Until now nothing read it. A refusal that already knew which clauses of an
 * objective failed, or which values the build accepts, put them on the wire and
 * the page showed the sentence alone.
 *
 * Returns null rather than `{}` so "no structured data" stays distinguishable
 * from "an empty object", and never throws: an arbitrary rejection reaches this
 * function, including a bare string, undefined, and an axios network failure.
 * An array is rejected too — `detail.data` is an object by construction, and a
 * caller indexing one by key would read `undefined` off every field rather than
 * noticing the shape was wrong.
 */
export function extractErrorData(
  error: unknown,
): Record<string, unknown> | null {
  const detail = (error as any)?.response?.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const data = detail.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data;
}

/**
 * The error code an API refusal names itself with, e.g. `RB422ROI`.
 *
 * Callers use it to tell refusals apart when the difference matters — the
 * sentence is written for a human and is not a stable key.
 */
export function extractErrorCode(error: unknown): string | null {
  const detail = (error as any)?.response?.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const code = detail.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}
